import { useMutation } from "@apollo/client/react"
import { Button, Dialog, DialogTitle, DialogActions, CircularProgress } from "@mui/material"
import React, { useState } from "react"

import { RemoveImageDocument, Associated } from "@/graphql/graphql"
import i18n from "@/i18n"
import { showErrorToast } from "@/shared/toast"

interface PhotoItemProps {
  image: {
    id: string
    url?: string | null
  }
  venueId: string
  onPreview: (url: string) => void
  onRemoveSuccess: () => void
}

const STYLES = {
  container: "relative w-full pb-[50%] rounded-lg overflow-hidden cursor-pointer",
  image: "absolute inset-0 w-full h-full object-cover",
  hoverOverlay: "absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center gap-2",
} as const

const PhotoItem: React.FC<PhotoItemProps> = ({ image, venueId, onPreview, onRemoveSuccess }) => {
  const [isHovered, setIsHovered] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [removeImageMutation, { loading: removing }] = useMutation(RemoveImageDocument)

  const handleRemove = async () => {
    try {
      const result = await removeImageMutation({
        variables: {
          imageId: image.id,
          associatedId: venueId,
          associatedType: Associated.Venue,
        },
      })

      if (result.data?.removeImage?.success) {
        onRemoveSuccess()
        setShowConfirmDialog(false)
      }
    } catch (err) {
      showErrorToast("Failed to remove photo. Please try again.")
      console.error("Error removing image:", err)
    }
  }

  return (
    <>
      <div
        className={STYLES.container}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid={`settings-venue-photo-${image.id}`}
      >
        <img src={image.url || ""} alt="Venue" className={STYLES.image} />
        {isHovered ? (
          <div className={STYLES.hoverOverlay}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => onPreview(image.url || "")}
              data-testid={`settings-venue-photo-${image.id}-btn-preview`}
            >
              Preview
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => setShowConfirmDialog(true)}
              disabled={removing}
              data-testid={`settings-venue-photo-${image.id}-btn-remove`}
            >
              {removing ? <CircularProgress size={20} /> : "Remove"}
            </Button>
          </div>
        ) : null}
      </div>

      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        data-testid={`settings-venue-photo-${image.id}-confirm-dialog`}
      >
        <DialogTitle>{i18n.t("app.settings.venues.tabbio.photos.remove_confirmation")}</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => setShowConfirmDialog(false)}
            color="inherit"
            data-testid={`settings-venue-photo-${image.id}-btn-confirm-cancel`}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemove}
            color="error"
            disabled={removing}
            data-testid={`settings-venue-photo-${image.id}-btn-confirm-remove`}
          >
            {removing ? <CircularProgress size={20} /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default PhotoItem
